"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheAtticToBasementDifference() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const components = [{
    id: "perimeter",
    label: "The Perimeter",
    number: "01",
    name: "Exterior Perimeter Treatment",
    description: "We apply targeted treatments around the exterior of your home, including the foundation line, to help keep pests from getting inside.",
    specs: [],
    top: "42%",
    left: "48%"
  }, {
    id: "entry",
    label: "Entry Points",
    number: "02",
    name: "Entry Point Treatments",
    description: "We apply targeted treatments around doors, windows, garage doors, utility penetrations, vents, and eaves, and we seal small cracks and small holes, to help keep pests from getting inside.",
    specs: [{
      label: "Focus",
      value: "Doors, windows, vents, eaves"
    }, {
      label: "Exclusion",
      value: "Small cracks and holes only"
    }],
    top: "55%",
    left: "38%"
  }];
  const [activeId, setActiveId] = useState<string>("perimeter");
  const active = components.find(c => c.id === activeId) ?? components[0];
  return <section data-section-id="5188" id="the-attic-to-basement-difference" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      <div ref={ref} className={`mx-auto max-w-[1400px] px-3 sm:px-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div className="relative lg:col-span-7 lg:-ml-6">
            <div className="relative w-full h-[50vh] sm:h-[60vh] lg:h-[90vh] overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_control_precision_macro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_control_precision_macro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_control_precision_macro.png-wvc-sizes" alt="Attic to Basement Pest Control technical inspection" width={800} height={900} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-background/40" />

              {}
              <div className="hidden lg:block">
                {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} aria-label={`View details for ${c.label}`} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{
                top: c.top,
                left: c.left
              }}>
                    <span className={`absolute inset-0 -m-3 rounded-md ${activeId === c.id ? "animate-ping bg-primary/30" : "bg-primary/10 animate-pulse"}`} />
                    <span className={`relative block size-3 rounded-md border transition-all duration-300 ${activeId === c.id ? "bg-primary border-primary scale-125" : "bg-primary/60 border-primary/80 group-hover:scale-110"}`} />
                  </button>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-8">
            <div className="font-default text-xs uppercase tracking-widest text-primary font-bold mb-6">
              01 // PROTECTION
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-7xl leading-[0.95] text-foreground mb-10">
              The <em className="italic font-light text-primary">Sanctuary</em>
              <br />
              Defense
            </h2>

            <p className="font-default text-base text-muted-foreground leading-relaxed max-w-md mb-12">
              Our approach protects your home from the outside in. Explore the layers of our Attic to Basement defense strategy.
            </p>

            {}
            <div key={active.id} className="relative border border-border p-8 lg:p-10 transition-opacity duration-400 animate-in fade-in bg-card">
              <div className="absolute -top-3 left-8 bg-background px-3 font-default text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
                {active.label} / {active.number}
              </div>

              <h3 className="font-serif text-3xl lg:text-4xl text-foreground mb-4 leading-tight font-semibold">
                {active.name}
              </h3>

              <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8">
                {active.description}
              </p>

              {}
              <div className="border border-border/60 bg-muted/40 p-5 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    FIG. {active.number} // MAPPING
                  </span>
                  <Plus className="size-3 text-primary" />
                </div>
                <div className="relative h-24 flex items-center justify-center">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
                  <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                  <div className="relative size-16 border border-primary/60 rounded-md flex items-center justify-center">
                    <div className="size-8 border border-primary/40 rounded-md" />
                  </div>
                </div>
              </div>

              {/* Specs only rendered when present */}
              {active.specs && active.specs.length > 0 && <div className="space-y-3">
                  {active.specs.map((s, i) => <div key={s.label} data-index={i} className="flex items-baseline justify-between border-b border-border/50 pb-3 last:border-b-0">
                      <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                        {s.label}
                      </span>
                      <span className="font-mono text-sm tabular-nums text-foreground font-medium">
                        {s.value}
                      </span>
                    </div>)}
                </div>}
            </div>
          </div>
        </div>

        {/* Nav tabs for desktop */}
        <div className="hidden lg:grid grid-cols-2 mt-20 border-t border-border pt-8">
          {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} className={`group text-left transition-colors duration-300 ${activeId === c.id ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2">
                {c.number}
              </div>
              <div className="font-default text-xs font-bold uppercase tracking-widest">
                {c.label}
              </div>
              <div className={`mt-3 h-px transition-all duration-500 ${activeId === c.id ? "bg-primary w-full" : "bg-border w-6 group-hover:w-full group-hover:bg-primary/40"}`} />
            </button>)}
        </div>

        {}
        <div className="lg:hidden mt-10 -mx-6 px-6 overflow-x-auto">
          <div className="flex gap-3 pb-2 w-max">
            {components.map((c, i) => <button key={c.id} data-index={i} onClick={() => setActiveId(c.id)} className={`shrink-0 border px-3 py-2 sm:px-4 sm:py-3 text-left transition-colors duration-300 rounded-md ${activeId === c.id ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
                  {c.number}
                </div>
                <div className="font-default text-xs font-bold uppercase tracking-widest">
                  {c.label}
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}